import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import { BrushCursor, CURSOR_SWITCH_THRESHOLD } from '../../../../icons';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { DEFAULT_BRUSH_SIZE, SEGMENTATION_TOOLS } from '../../../constants';
import useCanvas from '../../../providers/hooks/use-canvas';
import useStage from '../../../providers/hooks/use-stage';
import { type StagePointerEventT } from '../../../providers/stage-provider';
import type { BrushAnnotationT } from '../../../types';
import { applyColorToImageData, clearCanvas, drawBitmap, drawLine, fillDrawing } from '../canvas/utils';
const Cursor = styled<'div', {
  $size: number;
}>('div', ({
  $size
}) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  marginTop: `-${$size / 2}px`,
  marginLeft: `-${$size / 2}px`,
  cursor: 'none'
}));
type PropsT = {
  isDrawing?: boolean;
  annotation?: BrushAnnotationT | null;
  annotationIdx?: string;
  annotationBitmap?: ImageData;
  onPaintComplete: (() => void) | (() => Promise<void>);
  onPaintStart?: () => void;
};
const Brush = ({
  isDrawing,
  annotation,
  annotationBitmap,
  onPaintComplete,
  onPaintStart
}: PropsT) => {
  const cursorRef = useRef<HTMLDivElement | undefined | null>(null);
  const {
    stage,
    getStageSettings
  } = useStage();
  const stageSettings = getStageSettings();
  const [isPainting, setIsPainting] = useState(false);
  const {
    getDrawingCanvasContext
  } = useCanvas();
  const {
    brushToolSettingsAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const {
    selectedTool,
    size,
    eraserSelected
  } = useAtomValue(brushToolSettingsAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const scaledSize = size ? size / stageSettings.scale : DEFAULT_BRUSH_SIZE;
  const onPointerDown = useCallback(
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  (stageEvent: StagePointerEventT) => {
    // start annotation
    const {
      mousePos
    } = stageEvent.detail;
    const drawingCtx = getDrawingCanvasContext();
    // TODO: handle case where annotationLabel is not selected
    if (!annotationLabel || !drawingCtx) return;
    clearCanvas(drawingCtx);
    if (annotation) {
      const existingBitmap = annotationBitmap;
      if (existingBitmap) {
        applyColorToImageData(existingBitmap, annotationLabel.style.strokeColor);
        drawBitmap(drawingCtx, existingBitmap);
      }
    }
    if (!isPainting) {
      drawLine({
        ctx: drawingCtx,
        point: mousePos,
        size: scaledSize,
        color: annotationLabel.style.strokeColor,
        eraserSelected,
        moveFirstPoint: mousePos
      });
      setIsPainting(true);
      onPaintStart?.();
    }
  }, [getDrawingCanvasContext, annotationLabel, annotation, scaledSize, eraserSelected, onPaintStart]);
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    // update top and left css of cursor ref to mouse Pos
    if (cursorRef.current) {
      cursorRef.current.style.top = `${stageEvent.detail.mousePos[1]}px`;
      cursorRef.current.style.left = `${stageEvent.detail.mousePos[0]}px`;
      cursorRef.current.style.marginTop = `-${scaledSize / 2}px`;
      cursorRef.current.style.marginLeft = `-${scaledSize / 2}px`;
    }
    const drawingCtx = getDrawingCanvasContext();
    if (!annotationLabel || !drawingCtx || !isPainting) return;
    const {
      mousePos
    } = stageEvent.detail;
    drawLine({
      ctx: drawingCtx,
      point: mousePos,
      size: scaledSize,
      color: annotationLabel.style.strokeColor,
      eraserSelected
    });
  }, [getDrawingCanvasContext, annotationLabel, isPainting, scaledSize, eraserSelected]);
  const onPointerUp = useCallback((stageEvent: StagePointerEventT) => {
    setIsPainting(false);
    const drawingCtx = getDrawingCanvasContext();
    if (!annotationLabel || !drawingCtx) return;
    if (selectedTool === SEGMENTATION_TOOLS.SEGMENTATION_POLYGON) {
      fillDrawing(drawingCtx, annotationLabel.style.strokeColor);
    }
    drawingCtx.closePath();
    onPaintComplete();
  }, [getDrawingCanvasContext, annotationLabel, selectedTool, onPaintComplete]);
  useEffect(() => {
    if (!stage) return;
    if (isDrawing) {
      stage.addEventListener('pointerdown', onPointerDown);
      stage.addEventListener('pointermove', onPointerMove);
      stage.addEventListener('pointerup', onPointerUp);
      return () => {
        stage.removeEventListener('pointerdown', onPointerDown);
        stage.removeEventListener('pointermove', onPointerMove);
        stage.removeEventListener('pointerup', onPointerUp);
      };
    }
  }, [isDrawing, stage, onPointerDown, onPointerMove, onPointerUp]);
  return <>
      {scaledSize > CURSOR_SWITCH_THRESHOLD &&
    // @ts-expect-error missing null check
    <Cursor data-testid="cursor" ref={cursorRef} $size={scaledSize}>
          <BrushCursor size={scaledSize} />
        </Cursor>}
    </>;
};
export default Brush;